import { Injectable, OnDestroy } from '@angular/core';
import { NavigationCancel, Router } from '@angular/router';

import {
    BehaviorSubject,
    combineLatest,
    Observable,
    Subject,
    Subscription,
    filter as rxjsFilter,
    map,
    mapTo,
    take,
    tap,
    switchMap,
    of,
    filter,
    catchError,
    finalize,
} from 'rxjs';
import moment from 'moment-timezone';

import { DropdownOptionModel } from '../../../Common/Forms/models/dropdown-option.model';
import { LeftSidebarListItem, LeftSidebarSubitem } from '../../../Common/Layout/models/left-sidebar-list-item.model';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { LeftSidebarLocalizations } from '../../../Common/Layout/models/left-sidebar-localizations.model';
import { LeftSidebarService } from '../../../Common/Layout/services/left-sidebar.service';
import { Mode } from '../../../Common/Layout/enums/mode.enum';
import { MonitoringRoutingService } from './monitoring-routing.service';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { FolderService } from './folder.service';
import { BriefingsService } from '../../Briefings/services/briefings.service';
import { BriefingListItemModel } from '../../../Common/MonitorShared/model/briefing-list-item.model';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { MediaSubscriptionService } from './media-subscription.service';
import { BriefingStatusEnum } from '../../../Common/MonitorShared/enum/briefing-status.enum';
import { BriefingDeliveryMethodEnum } from '../../Briefings/enums/briefing-delivery-method.enum';
import { AlertsService } from '../../Alerts/services/alerts.service';
import { automationAgentsRoutePath } from '../../../AutomationAgents/AutomationAgentsRouter/automation-agents-route.const';
import { AgentSourceIndexService } from '../../../AutomationAgents/services/agent-source-index.service';
import { MonitoringSeriesTypeEnum } from '../../Shared/enums/monitoring-series-type.enum';
import { FilterTypeCheckboxModel } from '../../../Common/Filter/models/filter-type-checkbox.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { Folder } from '../../Shared/models/folder.model';
import { TagModel } from '../../../Common/Tags/models/tag.model';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { TopicsService } from './topics.service';
import { MentionSearchListItemModel } from '../../Shared/models/mention-search-list-item.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FilterTypeAutoTaggingTopicsCheckboxModel, TaggingEnum } from '../../../Common/Filter/models/filter-type-auto-tagging-topics-checkbox.model';
import {
    AutoTaggingTopicsCheckboxFilterComponent
} from '../../../Common/Filter/components/AutoTaggingTopicsCheckboxFilter/auto-tagging-topics-checkbox-filter.component';

import { FilterTypeRadioModel } from '../../../Common/Filter/models/filter-type-radio.model';
import { ListEntitiesVisibilityEnum } from '../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { gateCall } from '../../../Core/rxjs-utils';
import { BriefingModel } from '../../Briefings/models/briefing.model';
import { MediaSubscription } from '@Monitor/Shared/models/media-subscription.model';
import { FilterTypeProviderServiceInterface } from '../../../Common/MonitorShared/misc/filter-type-provider-service.interface';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { LeftSidebarListItemConvertableModel } from '../../../Common/Layout/models/left-sidebar-list-item-convertable.model';
import { MonitoringLeftSidebarConfiguration, MonitoringLeftSidebarModeConfigOption } from '../models/monitoring-leftsidebar.config';
import { UserAppMonitorPreferencesModel, UserAppPreferencesModel } from '../../../Core/Users/models/user-app-preferences.model';
import { FilterOptionEnumLabel } from '../../../Common/MonitorShared/misc/filter-option-enum-label';
import { MonitorReportsNavigationService } from '../../../Common/MonitorShared/services/monitor-reports-navigation.service';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { IncludeExcludeEnum } from '@Common/Filter/enums/include-exclude.enum';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { AlertTypeEnum } from '../../../Common/MonitorShared/enum/alert-type.enum';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { TextInputDialogComponent } from '@Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '@Common/Dialogs/models/text-input-dialog.model';
import { ConfirmDialogModel } from '@Common/Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '@Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MonitoringViewModesEnum } from '@Monitor/Shared/enums/monitoring-view-modes.enum';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { MediaSubscriptionApiService } from '../../../api/mediaSubscriptions/media-subscription.api.service';
import { MediaSubscriptionBackfill } from '@Monitor/MediaSubscriptions/models/media-subscription-backfill.model';
import { ConfirmDialogAlertMessage } from '@Common/Dialogs/models/confirm-dialog-alert-message.model';
import { BackfillStatusApiEnum } from '@api/mediaSubscriptions/enums/backfill-status.api.enum';





type GenericLeftSidebarItem = AuditableModel & LeftSidebarListItemConvertableModel;
type StatusLeftSidebarItem = GenericLeftSidebarItem & { status: string };
type TypeLeftSidebarItem = GenericLeftSidebarItem & { type: string };
type AlertTypeLeftSidebarItem = GenericLeftSidebarItem & { alertType: string };
type BriefingDeliveryMethodLeftSidebarItem = GenericLeftSidebarItem & { deliveryMethod: string };

/** The modes whose lists hold something an automation agent can watch, by the type it is stored as */
const modeToAgentSourceTypeMap = new Map<Mode, MonitoringSeriesTypeEnum>([
    [Mode.newSearch, MonitoringSeriesTypeEnum.topic],
    [Mode.topics, MonitoringSeriesTypeEnum.topic],
    [Mode.mentions, MonitoringSeriesTypeEnum.folder],
    [Mode.mediaSubscriptions, MonitoringSeriesTypeEnum.subscription]
]);

const monitoringModeToUserPreferenceTypeMap = new Map<Mode, (keyof UserAppMonitorPreferencesModel | 'reportsPrefs')>([
    [Mode.mentions, 'savedCoveragePrefs'],
    [Mode.topics, 'topicsPrefs'],
    [Mode.briefings, 'briefingsPrefs'],
    [Mode.alerts, 'coverageAlertsPrefs'],
    [Mode.mediaSubscriptions, 'subscriptionsPrefs'],
]);

@Injectable()
export class MonitoringLeftSidebarService extends LeftSidebarService implements OnDestroy, FilterTypeProviderServiceInterface {
    private allMentionsKey: number = -1;
    private subscriptionsDashboardKey: number = -1;
    /** How many agents are live on each source, keyed by type and id - see AgentSourceIndexService */
    private liveAgentCounts: Map<string, number> = new Map<string, number>();

    private dropdownItems: DropdownOptionModel[];
    private dropdownItemsProvider: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);
    private selectedDropdownItem: DropdownOptionModel;
    private selectedDropdownItemProvider: BehaviorSubject<DropdownOptionModel> = new  BehaviorSubject<DropdownOptionModel>(null);

    private hiddenListItems: LeftSidebarListItem[];
    private hiddenListItemsProvider: BehaviorSubject<LeftSidebarListItem[]>;
    private listIsComplete: boolean;
    private listIsCompleteProvider: BehaviorSubject<boolean>;
    private selectedListItem: any | null;
    private selectedListItemProvider: BehaviorSubject<any | null>;
    private selectedSubitem: LeftSidebarSubitem;
    private reorderEnabled: boolean;
    private reorderEnabledProvider: BehaviorSubject<boolean>;
    private buttonEnabled: boolean;
    private buttonEnabledProvider: BehaviorSubject<boolean>;
    subitems$: Observable<LeftSidebarSubitem[]> = combineLatest([
        this.subitemsSubject.pipe(filter(x => !!x)),
        this.currentUserStateService.getPreferences().pipe(filter(x => !!x))])
        .pipe(switchMap(([id, preferences]) => {
            switch (this.currentRoute.type) {
                case RouteType.briefingsHype:
                case RouteType.briefing:
                case RouteType.canvas:
                case RouteType.briefingReport:
                case RouteType.recurringBriefingReport:
                case RouteType.canvasReport:
                case RouteType.recurringCanvasReport:
                case RouteType.alertsHype:
                case RouteType.alert:
                case RouteType.recurringAlertReport:
                    return this.briefingsService.getSentResources(id).pipe(
                        map(x => {
                            return x.sentDates.map(item => {
                                const subItem = new LeftSidebarSubitem();

                                switch (this.currentRoute.type) {
                                    case RouteType.alertsHype:
                                    case RouteType.alert:
                                    case RouteType.recurringAlertReport:
                                        subItem.id = item.guid;
                                        break;
                                    default:
                                        subItem.id = item.briefingSentId;
                                        break;
                                }

                                subItem.resourceId = x.briefingId;

                                if (preferences.timeZone) {
                                    subItem.label = moment(item.sentDate).tz(preferences.timeZone).format(preferences.getDateFormatString(true, true, true));
                                } else {
                                    subItem.label = moment(item.sentDate).format(preferences.getDateFormatString(true, true, true));
                                }
                                return subItem;
                            });
                        }),
                        // have to add catchError, or else if the getSentResources(id) fails, subitems$ will never "trigger" again
                        catchError(error => {
                            return of([]);
                        })
                    );
                default:
                    return of([]);
            }
        }));


    private localizations: LeftSidebarLocalizations;
    private localizationsProvider: BehaviorSubject<LeftSidebarLocalizations>;

    private readonly topics: MentionSearchListItemModel[] = [];
    private readonly mediaSubscriptions: MediaSubscription[] = [];
    private readonly subscriptionDashboardListItem: LeftSidebarListItem = new LeftSidebarListItem();
    private readonly folders: Folder[] = [];
    private readonly allMentionsListItem: LeftSidebarListItem = new LeftSidebarListItem();
    private readonly briefings: BriefingListItemModel[] = [];
    private readonly alerts: BriefingListItemModel[] = [];
    private tags: TagModel[];

    private subscriptions: Subscription[] = [];

    private itemActionHide: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye-slash',
        key: ActionsEnum.hide,
        tooltip: 'Monitor.leftSidebar.listItemAction.hide',
        label: 'Lhp.actions.hide',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.hide, icon: ''})
    };
    private itemActionShow: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-eye',
        key: ActionsEnum.show,
        tooltip: 'Monitor.leftSidebar.listItemAction.show',
        label: 'Lhp.actions.show',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.show, icon: ''})
    };

    private itemActionEdit: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.edit,
        tooltip: 'Monitor.leftSidebar.listItemAction.edit',
        label: 'Lhp.actions.edit',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.edit, icon: ''})
    };
    private itemActionRename: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-pen',
        key: ActionsEnum.rename,
        tooltip: 'Monitor.leftSidebar.listItemAction.rename',
        label: 'Lhp.actions.rename',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.rename, icon: ''})
    };
    private itemActionDelete: LeftSidebarListItemButton = {
        icon: 'fa-regular fa-duotone fa-trash-can',
        key: ActionsEnum.delete,
        tooltip: 'Monitor.leftSidebar.listItemAction.delete',
        label: 'Lhp.actions.delete',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.delete, icon: ''})
    };
    private itemActionCopy: LeftSidebarListItemButton = {
        icon: 'fa-kit-duotone fa-agpr-copy',
        key: ActionsEnum.copy,
        tooltip: 'Monitor.leftSidebar.listItemAction.copy',
        label: 'Lhp.actions.copy',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.copy, icon: ''})
    };
    private itemActionMove: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.move,
        tooltip: 'Monitor.leftSidebar.listItemAction.move',
        label: 'Lhp.actions.move',
        command: (event: any) => this.moveListItem(event.data)
    };

    private itemActionFavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.favorite,
        tooltip: 'Monitor.leftSidebar.listItemAction.favorite',
        label: 'Lhp.actions.favorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.favorite, icon: ''})
    };
    private itemActionUnfavorite: LeftSidebarListItemButton = {
        icon: '',
        key: ActionsEnum.unfavorite,
        tooltip: 'Monitor.leftSidebar.listItemAction.unfavorite',
        label: 'Lhp.actions.unfavorite',
        command: (event: any) => this.listItemAction(event.data, {key: ActionsEnum.unfavorite, icon: ''})
    };

    private showNewAlertDialogProvider: Subject<boolean>;
    private showNewTopicDialogProvider: Subject<boolean>;

    private mediaSubscriptionsProvisioned: boolean = false;

    private currentRoute: MonitoringRouteModel;
    private userPrefs: UserAppPreferencesModel;

    // TODO: tags will be added to various types of content later, so separating this because topics will require 2 tag filters
    // private readonly tagsFilter = new FilterTypeCheckboxModel<number>(
    //     'tags',
    //     'Tags.tags',
    //     [],
    //     true
    // );
    private readonly autoTaggingFilter = new FilterTypeAutoTaggingTopicsCheckboxModel(
        'autotags',
        'Monitor.leftSidebar.topics.filter.autoTag',
        AutoTaggingTopicsCheckboxFilterComponent,
        [],
        undefined,
        true
    );
    private readonly statusFilter = new FilterTypeCheckboxModel<string>(
        'status',
        'Status',
        [],
        true
    );
    private readonly typeFilter = new FilterTypeCheckboxModel<string>(
        'type',
        'Type',
        [],
        true
    );

    private readonly alertTypeFilter = new FilterTypeCheckboxModel<string>(
        'alertType',
        'Alerts.alertType',
        [],
        true
    );
    protected readonly autoSavingFilter = new FilterTypeRadioModel<ListEntitiesVisibilityEnum>(
        'autoSaving',
        'Monitoring.filter.autoSaving',
        []
    );

    private readonly deliveryMethodFilter = new FilterTypeCheckboxModel<string>(
        'deliveryMethod',
        'Monitor.leftSidebar.briefings.type',
        [],
        true
    );

    private monitoringLeftSidebarModeConfigMap: MonitoringLeftSidebarConfiguration = new MonitoringLeftSidebarConfiguration([
        this.nameFilter,
        this.createdByFilter,
        this.createdDateFilter,
        this.modifiedByFilter,
        this.modifiedDateFilter,
        this.statusFilter,
        this.typeFilter,
        this.autoTaggingFilter,
        this.visibilityFilter,
        this.alertTypeFilter,
        this.autoSavingFilter,
        this.deliveryMethodFilter,
        this.displayGroupFilter
    ]);

    constructor(
        private monitoringRoutingService: MonitoringRoutingService,
        private monitorReportsNavigationService: MonitorReportsNavigationService,
        private router: Router,
        private mediaSubscriptionService: MediaSubscriptionService,
        private folderService: FolderService,
        private briefingsService: BriefingsService,
        private alertsService: AlertsService,
        protected usersService: UsersService,
        private tagsService: TagsService,
        private topicsService: TopicsService,
        private currentUserStateService: CurrentUserStateService,
        private coreDialogService: CoreDialogService,
        private userPreferencesService: UserPreferencesService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService,
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private agentSourceIndexService: AgentSourceIndexService
    ) {
        super(usersService, displayGroupsService, localizationService);

        this.registerLeftSidebarConfigs();

        this.setFilters([
            this.nameFilter,
            this.createdByFilter,
            this.modifiedByFilter,
            this.visibilityFilter
        ]);

        this.showNewAlertDialogProvider = new Subject<boolean>();
        this.showNewTopicDialogProvider = new Subject<boolean>();

        this.currentMode = Mode.topics;
        const config = this.monitoringLeftSidebarModeConfigMap.getConfig(this.currentMode);

        this.listItems = null;
        this.listItemsProvider = new BehaviorSubject(this.listItems);
        this.listItemGroupsProvider = new BehaviorSubject(this.listItemGroups);
        this.hiddenListItems = null;
        this.hiddenListItemsProvider = new BehaviorSubject(this.hiddenListItems);
        this.listIsComplete = true;
        this.listIsCompleteProvider = new BehaviorSubject(this.listIsComplete);
        this.selectedListItem = null;
        this.selectedListItemProvider = new BehaviorSubject(this.selectedListItem);
        this.reorderEnabled = false;
        this.reorderEnabledProvider = new BehaviorSubject(this.reorderEnabled);
        this.buttonEnabled = false;
        this.buttonEnabledProvider = new BehaviorSubject(this.buttonEnabled);

        this.localizations = config.localizations;
        this.localizationsProvider = new BehaviorSubject(this.localizations);

        // For LHP Management Panel
        this.modeProvider$ = new BehaviorSubject(this.currentMode);

        this.allMentionsListItem.value = this.allMentionsKey;
        this.allMentionsListItem.label = 'Monitor.leftSideBar.savedMentions.allSavedMentions';
        this.allMentionsListItem.icon = 'fa-kit-duotone fa-agpr-view-coverage';

        this.subscriptionDashboardListItem.value = this.subscriptionsDashboardKey;
        this.subscriptionDashboardListItem.label = 'Monitor.leftSideBar.subscriptions.dashboard';
        this.subscriptionDashboardListItem.icon = 'fa-duotone fa-regular fa-gauge-high';

        this.topListItem = null;
        this.topListItemProvider = new BehaviorSubject(this.topListItem);

        this.toggleHideFilter(true);
        this.createSubscriptions();

        this.updateSortFields(this.currentMode);
    }


    /**
     * Updates the sidebar based on the route
     * the user has navigated to
    */
    updateFromRoute(route: MonitoringRouteModel) {
        this.currentRoute = route;
        this.selectedListItem = null;
        let mode = this.currentMode;


        switch (route.type) {
            case RouteType.search:
                this.primaryHeading = 'Monitor.primaryHeading.search';
                mode = Mode.newSearch;
                break;
            case RouteType.results:
            case RouteType.topicsHype:
            case RouteType.topic:
            case RouteType.topicAnalyticsDashboard:
            case RouteType.adHocAnalyticsSyndicates:
            case RouteType.topicAnalyticsSyndicates:
                mode = Mode.topics;
                this.primaryHeading = 'Monitor.hype.topics.headline';
                break;
            case RouteType.subscriptionsHype:
            case RouteType.subscription:
            case RouteType.subscriptionDashboard:
            case RouteType.subscriptionResults:
            case RouteType.subscriptionAnalyticsDashboard:
            case RouteType.subscriptionAnalyticsSyndicates:
                this.primaryHeading = 'Monitor.hype.subscriptions.headline';
                mode = Mode.mediaSubscriptions;
                break;
            case RouteType.folder:
            case RouteType.mentions:
            case RouteType.mentionsHype:
            case RouteType.folderAnalyticsDashboard:
            case RouteType.allSavedCoverageAnalyticsDashboard:
            case RouteType.folderAnalyticsSyndicates:
            case RouteType.allSavedCoverageAnalyticsSyndicates:
                this.primaryHeading = 'Monitor.hype.savedCoverage.headline';
                mode = Mode.mentions;
                break;
            case RouteType.briefingsHype:
            case RouteType.briefing:
            case RouteType.canvas:
            case RouteType.briefingReport:
            case RouteType.recurringBriefingReport:
            case RouteType.canvasReport:
            case RouteType.recurringCanvasReport:
                this.primaryHeading = 'Monitor.hype.briefings.headline';
                mode = Mode.briefings;
                break;
            case RouteType.alertsHype:
            case RouteType.alert:
            case RouteType.recurringAlertReport:
                this.primaryHeading = 'Monitor.hype.alerts.headline';
                mode = Mode.alerts;
                break;
            default:
                this.primaryHeading = 'Monitor.hype.topics.headline';
                mode = Mode.topics;
                break;
        }

        for (const item of this.dropdownItems) {
            if (item.value === mode || (mode === Mode.newSearch && item.value === Mode.topics)) {
                this.selectedDropdownItem = item;
                this.selectedDropdownItemProvider.next(this.selectedDropdownItem);
            }
        }
        this.setSelectedListItem(route);

        if (mode !== this.currentMode) {
            this.setMode(mode);
        }

        this.selectedListItemProvider.next(this.selectedListItem);
        this.primaryHeading$.next(this.primaryHeading);
    }

    /**
     * Creates the subscriptions relating to advanced monitoring only. Does not include those common to AdvMon and MonLite
     */
    private createAdvancedMonitoringSubscriptions(): void {
        this.subscriptions.push(
            this.mediaSubscriptionService.all$.subscribe(
                mediaSubscriptions => {
                    if (mediaSubscriptions?.length) {
                        this.mediaSubscriptions.length = 0;
                        this.mediaSubscriptions.push(...mediaSubscriptions);
                    }
                }
            ),
            this.topicsService.getTopics().subscribe(
                topics => {
                    if (topics) {
                        this.topics.length = 0;
                        this.topics.push(...topics);
                    }
                }
            ),
            this.alertsService.getAlertsList().subscribe(
                alerts => {
                    if (alerts?.length) {
                        this.alerts.length = 0;
                        this.alerts.push(...alerts);
                    }
                }
            )
        );
    }

    /**
     * Creates the subscriptions relating to both advanced monitoring and MonLite
     */
    private createCommonMonitoringSubscriptions(): void {
        this.subscriptions.push(
            this.folderService.all$.subscribe(
                (folders) => {
                    if (folders?.length) {
                        this.folders.length = 0;
                        this.folders.push(...folders);
                    }
                }
            ),
            this.briefingsService.getBriefingListItems().subscribe(
                briefingListItems => {
                    if (briefingListItems?.length) {
                        this.briefings.length = 0;
                        this.briefings.push(...briefingListItems);
                    }
                }
            ),
            this.folderService.newFolderId$.subscribe(
                folderId => {
                    this.newListItemId.next(folderId);
                }
            ),
            this.briefingsService.getBlockTypes().subscribe(
                () => {
                    if (this.currentMode === Mode.briefings) {
                        this.applyFilters();
                    }
                }
            ),
        );
    }

    /**
     * Creates the subscriptions
    */
    private createSubscriptions(): void {
        gateCall(this.currentUserStateService.hasAdvancedMonitoringAccess()).pipe(
            tap(() => this.createAdvancedMonitoringSubscriptions()),
            take(1)
        ).subscribe();
        gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
            tap(() => this.createCommonMonitoringSubscriptions()),
            take(1)
        ).subscribe();

        this.subscriptions.push(
            // Sources an agent is live on are marked in the list, so a change redraws it
            this.agentSourceIndexService.liveAgentCounts$.subscribe(counts => {
                this.liveAgentCounts = counts;

                if (this.listItems) {
                    this.applyFilters();
                }
            }),
            this.currentUserStateService.getAccount().subscribe(account => {

                this.dropdownItems = [
                    new DropdownOptionModel('Core.navigation.mediaIntelligence.topics', Mode.topics),
                    new DropdownOptionModel('Core.navigation.mediaIntelligence.coverage', Mode.mentions),
                    new DropdownOptionModel('Core.navigation.mediaIntelligence.briefings', Mode.briefings),
                    new DropdownOptionModel('Core.navigation.mediaIntelligence.alerts', Mode.alerts),
                    new DropdownOptionModel('Core.navigation.mediaIntelligence.reports', Mode.reports),
                ];
                const permissions = account?.permissions;
                if (permissions?.includes(userPermissionsEnum.mediaSubscriptions)) {
                    this.dropdownItems.splice(4, 0, new DropdownOptionModel('Core.navigation.mediaIntelligence.subscriptions', Mode.mediaSubscriptions));
                }
                if (permissions?.includes(userPermissionsEnum.agenticFeature)) {
                    this.dropdownItems.push(
                        new DropdownOptionModel('Core.navigation.mediaIntelligence.automationAgents', Mode.automationAgents)
                    );
                }

                this.dropdownItemsProvider.next(this.dropdownItems);
                if (this.currentRoute) {
                    // To handle race condition, this sometimes will be reached before this.currentRoute being set
                    this.updateFromRoute(this.currentRoute);
                }

                this.toggleHideFilter(false);
                this.toggleHideAmplifyButton(false);
            })
        );

        const updateFromRoute$ = this.monitoringRoutingService.getCurrentRoute().pipe(
            tap(route => {
                this.currentRoute = route;
                if (this.dropdownItems) {
                    this.updateFromRoute(route);
                }
                this.updateButtonEnabled();
            })
        );

        const getCurrentMode$ = updateFromRoute$.pipe(
            map(route => {
                if (!route.type) {
                    return null;
                }

                return this.currentMode;
            })
        );

        this.subscriptions.push(
            this.createApplyFilterStream(
                this.monitoringRoutingService.getModeChange(getCurrentMode$),
                this.getItemsAndPrepareFilters.bind(this),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    tap(userAppPrefs => {
                        const config = this.monitoringLeftSidebarModeConfigMap.getConfig(this.currentMode);
                        const preferenceType = monitoringModeToUserPreferenceTypeMap.get(this.currentMode);
                        if (preferenceType) {
                            if (preferenceType === 'reportsPrefs') {
                                config.setPreferenceFilters(userAppPrefs.reportsPrefs.defaultFilters);
                            } else {
                                if (userAppPrefs.monitoringPrefs[preferenceType] && userAppPrefs.monitoringPrefs[preferenceType]['defaultFilters']) {
                                    config.setPreferenceFilters(userAppPrefs.monitoringPrefs[preferenceType]['defaultFilters']);
                                }
                            }
                        }
                        this.userPrefs = userAppPrefs;
                    })
                ),
            ).subscribe(),
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationCancel) {
                    if (this.dropdownItems) {
                        this.updateFromRoute(this.currentRoute);
                    }
                    this.updateButtonEnabled();
                }
            }),
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions]).subscribe(
                (provisioned) => {
                    this.mediaSubscriptionsProvisioned = provisioned;
                    this.updateButtonEnabled();
                }
            ),
            this.tagsService.all$.subscribe(
                (tags) => {
                    this.tags = tags;
                    this.refreshTagFilterOptions();
                }
            ),
            this.selectedSubitemSubject.subscribe(subitem => {
                this.selectedSubitem = subitem;
                switch (this.currentRoute.type) {
                    case RouteType.briefingsHype:
                    case RouteType.briefing:
                    case RouteType.briefingReport:
                    case RouteType.recurringBriefingReport:
                        this.monitoringRoutingService.gotoRecurringBriefingReport(subitem.resourceId, subitem.id);
                        break;
                    case RouteType.canvas:
                    case RouteType.canvasReport:
                    case RouteType.recurringCanvasReport:
                        this.monitoringRoutingService.gotoRecurringCanvasReport(subitem.resourceId, subitem.id);
                        break;
                    case RouteType.alertsHype:
                    case RouteType.alert:
                    case RouteType.recurringAlertReport:
                        this.monitoringRoutingService.gotoRecurringAlertReport(subitem.resourceId, subitem.id);
                }
            })
        );
    }

    /**
     * Determines if the amplify button should be enabled based on the current mode.
     */
    private updateButtonEnabled(): void {
        let enabled: boolean = true;

        if (this.currentMode === Mode.mediaSubscriptions) {
            enabled = this.mediaSubscriptionsProvisioned;
        }

        this.buttonEnabled = enabled;
        this.buttonEnabledProvider.next(enabled);
    }

    /**
     * Sets selected list item based on the route.  If route value available, sets selected list item value
     * equal to the route value.  If no route value available but route type is all saved mentions it
     *  sets selectedListItem value equal to -1
     *
     * @param route the current route
     */
    private setSelectedListItem(route: MonitoringRouteModel): void {
        if (route.value) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = route.value;
        } else if (route.type === RouteType.mentions || route.type === RouteType.subscriptionDashboard) {
            this.selectedListItem = new LeftSidebarListItem();
            this.selectedListItem.value = -1;
        }
    }

    /**
     * Updates the current mode to the provided mode
     * and begins updating the sidebar to match
     * the provided mode's requirements
    */
    private setMode(mode: Mode, clearOldFilters: boolean = true): void {
        this.currentMode = mode;
        const config = this.monitoringLeftSidebarModeConfigMap.getConfig(this.currentMode);
        this.topListItem = null;
        if (mode === Mode.mentions) {
            this.toggleHideAmplifyButton(false);
            this.topListItem = this.allMentionsListItem;
        }

        if (mode === Mode.mediaSubscriptions) {
            this.topListItem = this.subscriptionDashboardListItem;
        }

        this.setLocalizations(config.localizations);
        if (clearOldFilters) {
            this.resetFilterOptions();
        }

        this.updateSortFields(mode);

        this.topListItemProvider.next(this.topListItem);

        this.modeProvider$.next(this.currentMode);
    }

    private prepareConfigFilters(config: MonitoringLeftSidebarModeConfigOption<LeftSidebarListItemConvertableModel>) {
        const placeholder = config.quickFilterPlaceholder;
        const items = config.listItemSource as GenericLeftSidebarItem[];
        const statusOptionLabel = config.getFilterOptionLabel('status');
        const alertTypeOptionLabel = config.getFilterOptionLabel('alertType');
        const typeOptionLabel = config.getFilterOptionLabel('type');
        const briefingTypeOptionLabel = config.getFilterOptionLabel('deliveryMethod');

        this.setQuickFilterPlaceholder(placeholder);
        this.getAuditableFilterOptions(items);

        if (this.filters.includes(this.autoTaggingFilter)) {
            this.getTagFilterOptions(this.autoTaggingFilter, items, this.getAutoTagIdsFromTopic);
        }

        if (this.filters.includes(this.statusFilter) && statusOptionLabel) {
            this.getStatusFilterOptions(items as StatusLeftSidebarItem[], statusOptionLabel);
        }

        if (this.filters.includes(this.alertTypeFilter) && alertTypeOptionLabel) {
            this.getAlertTypeFilterOptions(items as AlertTypeLeftSidebarItem[], alertTypeOptionLabel);
        }

        if (this.filters.includes(this.typeFilter) && typeOptionLabel) {
            this.getTypeFilterOptions(items as TypeLeftSidebarItem[], typeOptionLabel);
        }

        if (this.filters.includes(this.autoSavingFilter)) {
            this.getAutoSavingFilterOptions(this.autoSavingFilter);
        }

        if (this.filters.includes(this.deliveryMethodFilter) && briefingTypeOptionLabel) {
            this.getDeliveryMethodFilterOptions(items as BriefingDeliveryMethodLeftSidebarItem[], briefingTypeOptionLabel);
        }

        this.getVisibilityFilterOptions(this.visibilityFilter);
    }

    /**
     * Gets a list of items to include in the sidebar dropdown
    */
    getDropdownItems(): Observable<DropdownOptionModel[]> {
        return this.dropdownItemsProvider;
    }

    /**
     * Gets the currently selected dropdown item
    */
    getSelectedDropdownItem(): Observable<DropdownOptionModel> {
        return this.selectedDropdownItemProvider;
    }

    /**
     * Selects an item in the dropdown
     * @param item An item that exists in the dropdown
    */
    selectDropdownItem(item: DropdownOptionModel): void {
        if (this.dropdownItems.indexOf(item) < 0) {
            throw new Error('Item is not in the list');
        }
        this.selectedDropdownItem = item;
        this.selectedDropdownItemProvider.next(item);
        switch (item.value) {
            case Mode.topics:
                this.monitoringRoutingService.gotoTopics();
                break;
            case Mode.mediaSubscriptions:
                this.monitoringRoutingService.gotoSubscriptionHype();
                break;
            case Mode.mentions:
                this.monitoringRoutingService.gotoSavedCoverageHype();
                break;
            case Mode.briefings:
                this.monitoringRoutingService.gotoBriefings();
                break;
            case Mode.alerts:
                this.monitoringRoutingService.gotoAlerts();
                break;
            case Mode.reports:
                this.monitorReportsNavigationService.gotoReports();
                break;
            case Mode.automationAgents:
                this.router.navigate(['/', automationAgentsRoutePath]);
                break;
            case Mode.newSearch:
                this.monitoringRoutingService.gotoSearch();
                break;
            default:
                break;
        }
    }

    /**
     * Gets currently available items in the list
    */
    getListItems(): Observable<LeftSidebarListItem[]> {
        return this.listItemsProvider;
    }

    getHiddenListItems(): Observable<LeftSidebarListItem[]> {
        return this.hiddenListItemsProvider;
    }

    getListIsComplete(): Observable<boolean> {
        return this.listIsCompleteProvider;
    }

    getMoreListItems(): void {
        // not needed for folders right now
    }

    getSelectedListItem(): Observable<LeftSidebarListItem> {
        return this.selectedListItemProvider;
    }

    /**
     * Getter for the boolean to display dialog to create a new alert
     */
    getShowNewAlertDialogProvider(): Observable<boolean> {
        return this.showNewAlertDialogProvider;
    }

    /**
     * Getter for the boolean to display dialog to create a new topic
     */
    getShowNewTopicDialogProvider(): Observable<boolean> {
        return this.showNewTopicDialogProvider;
    }


    setVisibilityFilter(visibility: ListEntitiesVisibilityEnum = ListEntitiesVisibilityEnum.all) {
        this.visibilityFilter.setCurrentValue(visibility);
        this.applyFilters();
    }

    /**
     * Responds to a user selecting a list item
     * by taking the action that corresponds with
     * the current mode
    */
    selectListItem(item: LeftSidebarListItem): void {
        // if selecting the current active list item, return directly
        if (this.selectedListItem && JSON.stringify(item.value) === JSON.stringify(this.selectedListItem.value).replace(/"/g, '') && !this.selectedSubitem) {
            return;
        }

        switch (this.currentMode) {
            case Mode.newSearch:
            case Mode.topics:
                if (this.getResultViewMode() === MonitoringViewModesEnum.DASHBOARD) {
                    this.monitoringRoutingService.gotoTopicAnalyticsDashboard(item.value);
                } else if (this.getResultViewMode() === MonitoringViewModesEnum.SYNDICATE) {
                    this.monitoringRoutingService.gotoTopicAnalyticsSyndicates(item.value);
                } else {
                    this.monitoringRoutingService.gotoTopic(item.value);
                }
                break;
            case Mode.mediaSubscriptions:
                if (item.value === this.subscriptionsDashboardKey) {
                    this.monitoringRoutingService.gotoSubscriptionDashboard();
                } else {
                    if (this.getResultViewMode() === MonitoringViewModesEnum.DASHBOARD) {
                        this.monitoringRoutingService.gotoSubscriptionAnalyticsDashboard(item.value);
                    } else if (this.getResultViewMode() === MonitoringViewModesEnum.SYNDICATE) {
                        this.monitoringRoutingService.gotoSubscriptionAnalyticsSyndicates(item.value);
                    } else {
                        this.monitoringRoutingService.gotoMediaSubscriptionResults(item.value);
                    }
                }
                break;
            case Mode.mentions:
                if (item.value === this.allMentionsKey) {
                    if (this.getResultViewMode() === MonitoringViewModesEnum.DASHBOARD) {
                        this.monitoringRoutingService.gotoAllSavedCoverageAnalyticsDashboard();
                    } else if (this.getResultViewMode() === MonitoringViewModesEnum.SYNDICATE) {
                        this.monitoringRoutingService.gotoAllSavedCoverageAnalyticsSyndicates();
                    } else {
                        this.monitoringRoutingService.gotoSavedCoverage();
                    }
                } else {
                    if (this.getResultViewMode() === MonitoringViewModesEnum.DASHBOARD) {
                        this.monitoringRoutingService.gotoFolderAnalyticsDashboard(item.value);
                    }  if (this.getResultViewMode() === MonitoringViewModesEnum.SYNDICATE) {
                        this.monitoringRoutingService.gotoFolderAnalyticsSyndicates(item.value);
                    } else {
                        this.monitoringRoutingService.gotoFolder(item.value);
                    }
                }
                break;

            case Mode.briefings: {
                const briefingItem = item as unknown as BriefingDeliveryMethodLeftSidebarItem;
                const isCanvas = briefingItem.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE;
                if ((item.status === ('Briefings.status.' + BriefingStatusEnum.SENT)) ||
                    (item.status === ('Briefings.status.' + BriefingStatusEnum.SCHEDULED))
                ) {
                    isCanvas
                        ? this.monitoringRoutingService.gotoCanvasReport(item.value)
                        : this.monitoringRoutingService.gotoBriefingReport(item.value);
                } else if (isCanvas) {
                    this.monitoringRoutingService.gotoCanvas(item.value);
                } else {
                    this.monitoringRoutingService.gotoBriefing(item.value);
                }
                break;
            }
            case Mode.alerts:
                this.monitoringRoutingService.gotoAlert(item.value);
                break;

            default:
                break;
        }
    }

    getResultViewMode(): MonitoringViewModesEnum {
        switch (this.currentMode) {
            case Mode.newSearch:
            case Mode.topics:
                if (this.currentRoute.type === RouteType.topicAnalyticsDashboard) {
                    return MonitoringViewModesEnum.DASHBOARD;
                } else if (this.currentRoute.type === RouteType.topicAnalyticsSyndicates) {
                    return MonitoringViewModesEnum.SYNDICATE;
                }  else if (this.currentRoute.type === RouteType.topic) {
                    return MonitoringViewModesEnum.MENTION;
                } else {
                    return this.userPrefs.monitoringPrefs.topicsPrefs?.defaultDisplaySettings?.resultsViewMode ?? MonitoringViewModesEnum.MENTION;
                }
            case Mode.mediaSubscriptions:
                if (this.currentRoute.type === RouteType.subscriptionAnalyticsDashboard) {
                    return MonitoringViewModesEnum.DASHBOARD;
                } else if (this.currentRoute.type === RouteType.subscriptionAnalyticsSyndicates) {
                    return MonitoringViewModesEnum.SYNDICATE;
                } else if (this.currentRoute.type === RouteType.subscriptionResults) {
                    return MonitoringViewModesEnum.MENTION;
                } else {
                    return this.userPrefs.monitoringPrefs.subscriptionsPrefs?.defaultDisplaySettings?.resultsViewMode ?? MonitoringViewModesEnum.MENTION;
                }
            case Mode.mentions:
                if (this.currentRoute.type === RouteType.folderAnalyticsDashboard || this.currentRoute.type === RouteType.allSavedCoverageAnalyticsDashboard) {
                    return MonitoringViewModesEnum.DASHBOARD;
                } else if (
                    this.currentRoute.type === RouteType.folderAnalyticsSyndicates || this.currentRoute.type === RouteType.allSavedCoverageAnalyticsSyndicates
                ) {
                    return MonitoringViewModesEnum.SYNDICATE;
                } else if (this.currentRoute.type === RouteType.folder || this.currentRoute.type === RouteType.mentions) {
                    return MonitoringViewModesEnum.MENTION;
                } else {
                    return this.userPrefs.monitoringPrefs.savedCoveragePrefs?.defaultDisplaySettings?.resultsViewMode ?? MonitoringViewModesEnum.MENTION;
                }
            default:
                return MonitoringViewModesEnum.MENTION;
        }
    }

    /**
     * Responds to the user performing an action from
     * a sidebar list item based on the current mode
    */
    listItemAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean = true): void {
        if (!button.loading) {
            switch (this.currentMode) {
                case Mode.newSearch:
                case Mode.topics:
                case Mode.newSearch:
                    this.savedMentionSearchAction(item, button, fromLHP);
                    break;
                case Mode.mediaSubscriptions:
                    this.mediaSubscriptionAction(item, button, fromLHP);
                    break;
                case Mode.mentions:
                    this.mentionsFolderAction(item, button, fromLHP);
                    break;
                case Mode.briefings:
                    this.briefingAction(item, button, fromLHP);
                    break;
                case Mode.alerts:
                    this.alertAction(item, button, fromLHP);
                    break;
            }
        }
    }

    /**
     * Opens GroupSelectionDialog for user selection then adds item to the selected group.
     * @param item item to move on confirm
     */
    public moveListItem(item: LeftSidebarListItem) {
        const groupId = item.groupId ? item.groupId : null;
        const showUncategorized = !!item.groupId;
        this.displayGroupsService.openGroupSelectionDialog(groupId, showUncategorized).subscribe((group: DisplayGroupModel) => {
            switch(this.currentMode) {
                case Mode.newSearch:
                case Mode.topics:
                    this.topicsService.updateGroup([item.value], group);
                    break;
                case Mode.mentions:
                    // @HACK temp until all services are refactored
                    // eslint-disable-next-line rxjs/no-nested-subscribe
                    this.folderService.updateGroup([item.value], group, true).subscribe();
                    break;
                case Mode.briefings:
                    this.briefingsService.updateGroup([item.value], group);
                    break;
                case Mode.alerts:
                    this.alertsService.updateGroup([item.value], group);
                    break;
                case Mode.mediaSubscriptions:
                    // @HACK temp until all services are refactored
                    // eslint-disable-next-line rxjs/no-nested-subscribe
                    this.mediaSubscriptionService.updateGroup([item.value], group, true).subscribe();
                    break;
            }
        });
    }

    /**
     * Handles removing the groupId from all items in a deleted group
     * @param listItems items from the deleted group
     */
    public handleGroupDelete(ids: number[]) {
        switch(this.currentMode) {
            case Mode.newSearch:
            case Mode.topics:
                this.topicsService.removeAllTopicsFromGroup(ids);
                break;
            case Mode.mentions:
                this.folderService.removeFoldersFromGroup(ids).subscribe();
                break;
            case Mode.briefings:
                this.briefingsService.removeAllBriefingsFromGroup(ids);
                break;
            case Mode.alerts:
                this.alertsService.removeAllAlertsFromGroup(ids);
                break;
            case Mode.mediaSubscriptions:
                this.mediaSubscriptionService.removeSubscriptionsFromGroup(ids).subscribe();
                break;
        }
    }

    /**
     * Gets a provider for when reordering the left sidebar is enabled
     */
    getReorderEnabled(): Observable<boolean> {
        return this.reorderEnabledProvider;
    }

    /**
     * Sets the left sidebar's order
     */
    setListItems(items: LeftSidebarListItem[], listIsComplete?: boolean) {
        // for re-ordering
    }

    /**
     * Responds to the user clicking the primary call to action
     * in the sidebar
    */
    amplifyButtonClicked(): void {
        switch (this.currentMode) {
            case Mode.topics:
                this.showTopicAmplifyDialog();
                break;
            case Mode.mentions:
                this.folderService.openNewFolderDialog();
                break;
            case Mode.mediaSubscriptions:
                this.mediaSubscriptionService.navigateToNewMediaSubscription();
                break;
            case Mode.briefings:
                this.briefingsService.showCanLeaveDialog().subscribe(
                    result => {
                        if (result) {
                            this.monitoringRoutingService.gotoNewCanvas();
                        }
                    }
                );
                break;
            case Mode.alerts:
                this.showAlertAmplifyDialog();
                break;
            case Mode.newSearch:
            default:
                break;
        }
    }

    /**
     * Responds to the user clicking the primary call to action
     * in the sidebar
    */
    secondaryAmplifyButtonClicked(): void {
        switch (this.currentMode) {
            case Mode.briefings:
                this.showBriefingAmplifyDialog();
                break;
            default:
                break;
        }
    }

    /**
     * Checks if the primary button should be enabled.
     */
    public isAmplifyButtonEnabled(): Observable<boolean> {
        return this.buttonEnabledProvider;
    }

    /**
     * Opens the briefing create dialog, provided the user can leave the current briefing
     */
    public showBriefingAmplifyDialog(): void {

        this.briefingsService.showCanLeaveDialog().subscribe(
            result => {
                if (result) {
                    this.monitoringRoutingService.gotoNewBriefing();
                }
            }
        );
    }

    /**
     * Opens the briefing create dialog, provided the user can leave the current briefing
     */
    public goToNewOnlineBriefing(): void {
        this.briefingsService.showCanLeaveDialog().subscribe(
            result => {
                if (result) {
                    this.monitoringRoutingService.gotoNewCanvas();
                }
            }
        );
    }

    /**
     * Shows the alert amplify dialog, public so that the hype page can call it
     */
    public showAlertAmplifyDialog(): void {
        this.showNewAlertDialogProvider.next(true);
    }

    /**
     * Shows the topic amplify dialog
     */
    public showTopicAmplifyDialog(): void {
        this.showNewTopicDialogProvider.next(true);
    }


    /**
     * Gets the current localizations for the left sidebar
     */
    getLocalizations(): Observable<LeftSidebarLocalizations> {
        return this.localizationsProvider;
    }

    /**
     * Performs an action on a saved mention search
     * based on the provided action and item
    */
    private savedMentionSearchAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const search: MentionSearchListItemModel = this.topics.find(topic => topic.id === item.value);
        button.loading = false;

        switch (button.key) {
            case ActionsEnum.hide:
            case ActionsEnum.show:
                if (search) {
                    this.topicsService.updateVisibility(search.id, button.key, button);
                }
                break;
            case ActionsEnum.delete:
                this.topicsService.confirmDeleteTopic(search, fromLHP);
                break;
            case ActionsEnum.edit:
                this.topicsService.gotoTopicThenEdit(+item.value);
                break;
            case ActionsEnum.copy:
                this.topicsService.confirmCopyTopic(search);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    this.topicsService.openRenameTopicDialog(search);
                } else {
                    this.topicsService.renameTopic(search.id, item.label);
                }
                break;
            case ActionsEnum.favorite:
                this.topicsService.updateFavorites([search.id], []);
                break;
            case ActionsEnum.unfavorite:
                this.topicsService.updateFavorites([], [search.id]);
                break;
        }
    }

    /**
     * Performs an action on a media subscription
     * based on the provided action and item
    */
    private mediaSubscriptionAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const subscription = this.mediaSubscriptions.filter(x => x.id === item.value)[0];
        const action: string = button.key;
        const subId = item.value;
        const subName = item.label;

        button.loading = false;

        switch (action) {
            case ActionsEnum.hide:
            case ActionsEnum.show:
                if (subscription) {
                    this.mediaSubscriptionService.updateVisibility(subscription.id, action).subscribe();
                }
                break;
            case ActionsEnum.delete: {
                this.mediaSubscriptionApiService.getById(subscription.id).pipe(
                    tap(mediaSubscription => subscription.backfills = mediaSubscription.backfills?.map(x => new MediaSubscriptionBackfill(x)) || []),
                    finalize(() => {
                        const alertMessages: ConfirmDialogAlertMessage[] = [
                            { alertMessage: 'Monitoring.confirmDeleteSubscription', alertType: messageSeverityEnum.warning }
                        ];
                        const hasActiveBackfills = subscription.backfills.some(x => x.status === BackfillStatusApiEnum.inProgress);
                        if (hasActiveBackfills) {
                            alertMessages.push({
                                alertMessage: 'Monitoring.confirmDeleteSubscription.activeBackfillMessage',
                                alertType: messageSeverityEnum.warning
                            });
                        }
                        const io: ConfirmDialogModel = {
                            inputs: {
                                id: 'MediaSubscriptionsDelete',
                                heading: 'Monitoring.deleteSubscription',
                                message: this.localizationService.transform('Monitoring.confirmDeleteMediaSubscriptionMessage',
                                    { name: subscription.name }
                                ),
                                alertMessages: alertMessages,
                                okLabel: 'Core.button.delete'
                            },
                            outputs: {
                                dialogClose: (confirmed: boolean) => {
                                    if (confirmed) {
                                        this.mediaSubscriptionService.delete(subscription).subscribe();
                                    }
                                }
                            }
                        };
                        this.coreDialogService.open(ConfirmDialogComponent, io);
                    })
                ).subscribe();
                break;
            }
            case ActionsEnum.edit:
                this.monitoringRoutingService.gotoUpdateMediaSubscription(subscription.id);
                break;
            case ActionsEnum.copy:
                this.mediaSubscriptionService.openCopySubscriptionDialog(subscription);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    const io: TextInputDialogModel = {
                        inputs: {
                            id: 'renameSubscription',
                            heading: 'Monitoring.renameSubscriptionDialog.heading',
                            description: 'Monitoring.renameSubscriptionDialog.description',
                            placeholder: 'Monitoring.renameSubscriptionDialog.placeholder',
                            initialValue: subName,
                            okLabel: 'Lhp.actions.rename',
                            observableFormValidator: (name: string) => {
                                // @TODO see NEP-32699 for validation improvement
                                return this.mediaSubscriptionService.isNameDuplicated$(name, subId).pipe(
                                    map((isDuplicate) => {
                                        if (isDuplicate) {
                                            return ['MediaSubscriptions.saveMediaSubscriptionsDuplicateNameErrorMessage'];
                                        } else {
                                            return [];
                                        }
                                    })
                                );
                            },
                            submit: (name: string) => {
                                return this.mediaSubscriptionService.rename(subId, name);
                            },
                        }
                    };
                    this.coreDialogService.open(TextInputDialogComponent, io);
                } else {
                    this.mediaSubscriptionService.renameListDupCheck(subId, subName).subscribe();
                }
                break;
            case ActionsEnum.favorite:
                this.mediaSubscriptionService.updateFavorites([subscription.id], []).subscribe();
                break;
            case ActionsEnum.unfavorite:
                this.mediaSubscriptionService.updateFavorites([], [subscription.id]).subscribe();
                break;
        }
    }

    /**
     * Performs an action on a mentions folder
     * based on the action and folder provided
    */
    private mentionsFolderAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const folderId: number = item.value;
        const folder: Folder = this.folders.find(x => x.id === folderId);
        const action: string = button.key;
        const newfolderName: string = item.label;

        button.loading = false;

        switch (action) {
            case ActionsEnum.hide:
            case ActionsEnum.show: {
                if (folder) {
                    button.loading = true;
                    this.folderService.updateVisibility(folder.id, action).subscribe({
                        next: () => button.loading = false,
                        error: () => button.loading = false
                    });
                }

                break;
            }
            case ActionsEnum.delete: {
                if (folder?.totalMentions <= this.folderService.FOLDER_DELETION_MENTION_LIMIT) {
                    const io: ConfirmDialogModel = {
                        inputs: {
                            heading: 'Monitoring.deleteFolder',
                            alertMessage: 'Monitoring.confirmDeleteFolder',
                            message: this.localizationService.transform('Monitoring.confirmDeleteFolderMessage', { name: folder.name }),
                            alertType: messageSeverityEnum.warning,
                            okLabel: 'Core.button.delete',
                            id: 'FoldersDelete'
                        },
                        outputs: {
                            dialogClose: (confirmed: boolean) => {
                                if (confirmed) {
                                    if (item) {
                                        item.state = ActionsEnum.delete;
                                    }
                                    this.folderService.delete(folder).subscribe();
                                }
                            }
                        }
                    };
                    this.coreDialogService.open(ConfirmDialogComponent, io);

                } else if (folder?.totalMentions > this.folderService.FOLDER_DELETION_MENTION_LIMIT) {
                    const io: ConfirmDialogModel = {
                        inputs: {
                            heading: 'MentionFoldersService.deleteFolderExceededLimitErrorHeading',
                            okLabel: 'MentionFoldersService.deleteFolderExceededLimitErrorOkLabel',
                            showCancel: false,
                            alertType: messageSeverityEnum.error,
                            alertMessage: 'MentionFoldersService.deleteFolderExceededLimitErrorAlertMessage',
                            messageHtml: 'MentionFoldersService.deleteFolderExceededLimitErrorMessageHtml',
                        },
                        outputs: {
                            dialogClose: () => {}
                        }
                    };

                    this.coreDialogService.open(ConfirmDialogComponent, io);

                }

                break;
            }
            case ActionsEnum.rename: {
                if (fromLHP) {
                    const io: TextInputDialogModel = {
                        inputs: {
                            id: 'renameFolder',
                            heading: 'Monitoring.renameFolderDialogHeading',
                            okLabel: 'Lhp.actions.rename',
                            placeholder: 'Monitoring.folder.rename.placeholder',
                            description: 'Monitoring.folder.rename.name',
                            initialValue: folder.name,
                            observableFormValidator: (name: string) => {
                                return this.folderService.isNameDuplicated$(name, folderId).pipe(
                                    map((isDuplicate) => {
                                        if (isDuplicate) {
                                            return ['Monitoring.result.newFolder.error.duplicate'];
                                        } else {
                                            return [];
                                        }
                                    })
                                );
                            },
                            submit: (name: string) => {
                                return this.folderService.rename(folder.id, name);
                            }
                        }
                    };
                    this.coreDialogService.open(TextInputDialogComponent, io);
                } else {
                    this.folderService.renameListDupCheck(folder.id, newfolderName).subscribe();
                }

                break;
            }
            case ActionsEnum.copy:
                this.folderService.openCopyFolderDialog(folder);
                break;
            case ActionsEnum.favorite:
                this.folderService.updateFavorites([folder.id], []).subscribe();
                break;
            case ActionsEnum.unfavorite:
                this.folderService.updateFavorites([], [folder.id]).subscribe();
                break;
        }
    }

    /**
     * Performs an action on a briefing
     */
    private briefingAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const action: string = button.key;

        const briefingListItem: BriefingListItemModel = new BriefingListItemModel();
        briefingListItem.id = item.value;
        briefingListItem.status = item.status as BriefingStatusEnum;
        briefingListItem.name = item.label;
        briefingListItem.groupId = item.groupId;

        button.loading = false;

        switch (action) {
            case ActionsEnum.hide:
            case ActionsEnum.show:
                if (briefingListItem) {
                    this.briefingsService.updateBriefingVisibility(briefingListItem.id, action, button);
                }
                break;
            case ActionsEnum.delete:
                this.briefingsService.confirmDeleteBriefing(briefingListItem);
                break;
            case ActionsEnum.copy:
                this.briefingsService.confirmCopyBriefing(briefingListItem);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    this.briefingsService.openRenameBriefingDialog(briefingListItem);
                } else {
                    this.briefingsService.updateBriefingName(item.value, item.label);
                }
                break;
            case ActionsEnum.favorite:
                this.briefingsService.updateFavorites([briefingListItem.id], []);
                break;
            case ActionsEnum.unfavorite:
                this.briefingsService.updateFavorites([], [briefingListItem.id]);
                break;
        }
    }

    /**
     * Performs an action on an alert
     */
    private alertAction(item: LeftSidebarListItem, button: LeftSidebarListItemButton, fromLHP: boolean): void {
        const action: string = button.key;

        const alertListItem: BriefingListItemModel = new BriefingListItemModel();
        alertListItem.id = item.value;
        alertListItem.status = item.status as BriefingStatusEnum;
        alertListItem.name = item.label;
        alertListItem.groupId = item.groupId;

        button.loading = false;

        switch (action) {
            case ActionsEnum.hide:
            case ActionsEnum.show:
                if (alertListItem) {
                    this.alertsService.updateAlertVisibility(alertListItem.id, action, button);
                }
                break;
            case ActionsEnum.delete:
                this.alertsService.confirmDeleteAlert(alertListItem).subscribe();
                break;
            case ActionsEnum.copy:
                this.alertsService.confirmCopyAlert(alertListItem);
                break;
            case ActionsEnum.edit: // From LHP
                this.alertsService.renameAlert(alertListItem);
                break;
            case ActionsEnum.rename:
                if (fromLHP) {
                    this.alertsService.openRenameAlertDialog(alertListItem);
                } else {
                    this.alertsService.updateAlertName(item.value, item.label);
                }
                break;
            case ActionsEnum.favorite:
                this.alertsService.updateFavorites([alertListItem.id], []);
                break;
            case ActionsEnum.unfavorite:
                this.alertsService.updateFavorites([], [alertListItem.id]);
                break;
        }
    }

    /**
     * Sets the current localizations
     */
    private setLocalizations(localizations: LeftSidebarLocalizations): void {
        this.localizations = localizations;
        this.localizationsProvider.next(localizations);
    }

    //#region Filters

    /**
     * Resets all of the filters
     */
    private resetFilterOptions(): void {
        this.nameFilter.setValue([]);
        this.createdByFilter.setValue([]);
        this.createdByFilter.options = [];
        this.createdDateFilter.setValue(DateQueryModel.getAllQuery());
        this.modifiedByFilter.setValue([]);
        this.modifiedByFilter.options = [];
        this.modifiedDateFilter.setValue(DateQueryModel.getAllQuery());
        this.autoTaggingFilter.setValue([]);
        this.autoTaggingFilter.options = [];
        this.statusFilter.setValue([]);
        this.statusFilter.options = [];
        this.visibilityFilter.setValue(null);
        this.visibilityFilter.options = [];
        this.alertTypeFilter.setValue([]);
        this.deliveryMethodFilter.setValue([]);
        this.autoSavingFilter.setValue(null);
        this.autoSavingFilter.options = [];
    }


    private applyVisibilityFilter<T extends AuditableModel>(visFilter: FilterTypeRadioModel<ListEntitiesVisibilityEnum>, items: T[]): T[] {
        const value = visFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
                return items;
            case ListEntitiesVisibilityEnum.visible:
                return items.filter(item => !item.hidden);
            case ListEntitiesVisibilityEnum.hidden:
                return items.filter(item => item.hidden);
            default:
                return items.filter(item => !item.hidden);
        }
    }

    private applyAutoSavingFilter(items: MentionSearchListItemModel[]): MentionSearchListItemModel[] {
        const value = this.autoSavingFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
                return items;
            case ListEntitiesVisibilityEnum.visible:
                return items.filter(item => item.autoSavingEnabled);
            case ListEntitiesVisibilityEnum.hidden:
                return items.filter(item => !item.autoSavingEnabled);
            default:
                return items;
        }
    }

    /**
     * Gets the list of items that are currently "hidden" in the LHP, depending on visibility filter applied.
     * @param filter the visibility filter currently applied
     * @param items a list of LHP items
     * @returns a list of the currently hidden items based on the visibility filter
     */
    private getHiddenItemsFromFilteredItems<T extends AuditableModel>(
        visFilter: FilterTypeRadioModel<ListEntitiesVisibilityEnum>,
        items: T[]
    ): T[] {
        const value = visFilter.getValue();
        switch (value) {
            case ListEntitiesVisibilityEnum.all:
            case ListEntitiesVisibilityEnum.hidden:
                // There are no "hidden" items that are not currently showing if we are explicitly showing them
                return [];
            case ListEntitiesVisibilityEnum.visible:
            default:
                // can be null as there are no options/starting value for the visibility filter
                return items.filter(item => item.hidden);
        }
    }

    /**
     * Gets the list of tag ids from a topic (specifically, applyTagIds, not the search query ids)
     */
    getAutoTagIdsFromTopic(topic: MentionSearchListItemModel): number[] {
        return topic.applyTagIds;
    }

    getTopicsWithAutoTagging( topics: MentionSearchListItemModel[], autoTagType: TaggingEnum): MentionSearchListItemModel[]  {
        return topics.filter(topic => {
            if (autoTagType === TaggingEnum.auto) {
                return topic.autoTaggingSearch === true;
            } else {
                return topic.autoTaggingSearch === false;
            }
        });
    }

    /**
     * Gets the list of tag options for a list of items that have tags associated with them
     */
    public getTagFilterOptions<T>(
        tagFilter: FilterTypeCheckboxModel<number>,
        items: T[],
        getItemTagIds: (item: T) => number[]
    ): void {
        tagFilter.options = [];

        if (!this.tags || this.tags.length < 1 || !items) {
            return;
        }

        const tagCounts: {[key: number]: number} = {};

        items.forEach(item => {
            const itemTagIds = getItemTagIds(item);

            if (!itemTagIds) {
                return;
            }

            itemTagIds.forEach((itemTagId) => {
                if (tagCounts[itemTagId]) {
                    tagCounts[itemTagId]++;
                } else {
                    tagCounts[itemTagId] = 1;
                }
            });
        });
        const tagIds = Object.keys(tagCounts);
        tagFilter.options = tagIds.map((tagId) => {
            const foundTag = this.tags.find(tag => tag.id === +tagId);
            if (!foundTag) {
                return null;
            }
            return {
                label: foundTag.name,
                count: tagCounts[+tagId],
                value: foundTag.id,
                numResults: items.length
            };
        }).filter(opt => !!opt).sort((a, b) => b.count - a.count);

        const filtersChanged = this.removeUnavailableOptions(tagFilter);
        if (filtersChanged) {
            this.applyFilters();
        }
    }

    /**
     * Applies the tags filters to a list of items that have tag associations
     */
    public applyTagsFilter<T>(
        tagFilter: FilterTypeAutoTaggingTopicsCheckboxModel,
        originalItems: T[],
        getItemTagIds: (item: T) => number[],
        getItemsByAutoTagOption: (item: T[], autoTagType: TaggingEnum) => T[]
    ): T[] {
        let filteredItems = [];
        if (tagFilter.autoTagOptions && tagFilter.autoTagOptions.length > 0) {
            tagFilter.autoTagOptions.forEach(x => {
                if (x === TaggingEnum.auto) {
                    filteredItems.push(...getItemsByAutoTagOption(originalItems, TaggingEnum.auto));
                }
                if (x === TaggingEnum.onDemand) {
                    filteredItems.push(...getItemsByAutoTagOption(originalItems, TaggingEnum.onDemand));
                }
            });
        } else {
            filteredItems = originalItems.slice();
        }

        const filterTags = tagFilter.getValue();

        if (filterTags?.length === 0 && tagFilter.autoTagOptions?.length === 0) {
            return originalItems;
        }

        const includedItems = filteredItems.slice().filter(item => {
            const itemTags = getItemTagIds(item);
            if (!itemTags || itemTags.length < 1) {
                return false;
            } else if (itemTags.length > 0 && filterTags?.length === 0) {
                return true;
            } else if (filterTags?.length > 0) {
                return !!itemTags.find(itemTag => filterTags.includes(itemTag));
            }
        });

        const excludedItems = originalItems.slice().filter(item => {
            return !includedItems?.includes(item);
        });

        if (tagFilter.includeExclude === IncludeExcludeEnum.include) {
            return includedItems ? includedItems : [];
        }
        if (tagFilter.includeExclude === IncludeExcludeEnum.exclude) {
            return excludedItems ? excludedItems : [];
        }
    }

    /**
     * Gets the list of options for any string key from a list of objects
     *
     * @param filter A string checkbox filter
     * @param items A list of items
     * @param getItemOptionKey A method for extracting a string key
     * @param enumLabel The prefix for the filter option along with the enum to use to find it
     */
    getEnumStringOptions<T>(
        xFilter: FilterTypeCheckboxModel<string>,
        items: T[],
        getItemOptionKey: (item: T) => string,
        enumLabel: FilterOptionEnumLabel
    ): void {
        if (!items || items.length < 1) {
            xFilter.options = [];
            return;
        }

        // Initialize enum counts to 0 for all enum values
        const optionCounts = Object.values(enumLabel.enumType).reduce((o, prop) => {
            o[prop as string] = 0;
            return o;
        }, {} as { [key: string]: number});

        items.forEach(item => {
            const key = getItemOptionKey(item);

            if (!Object.values(enumLabel.enumType).includes(key)) {
                return;
            }

            if (optionCounts[key]) {
                optionCounts[key]++;
            } else {
                optionCounts[key] = 1;
            }
        });
        const keys = Object.keys(optionCounts);
        xFilter.options = keys.map(key => {
            return {
                label: enumLabel.prefix + key,
                count: optionCounts[key],
                value: key
            };
        }).sort((a, b) => b.count - a.count);

        const filtersChanged = this.removeUnavailableOptions(xFilter);
        if (filtersChanged) {
            this.applyFilters();
        }
    }

    /**
     * Gets the list of status options from a list of items that have a status
     * @param items A list of items
     * @param getItemStatus A method for extracting the status key
     * @param localePrefix A prefix for localizing the options
     */
    public getStatusFilterOptions<T extends {status: string}>(items: T[], optionLabel: FilterOptionEnumLabel): void {
        this.getEnumStringOptions(this.statusFilter, items, (item: T) => item.status, optionLabel);
    }

    public getAlertTypeFilterOptions<T extends {alertType: string}>(items: T[], optionLabel: FilterOptionEnumLabel): void {
        this.getEnumStringOptions(this.alertTypeFilter, items, (item: T) => item.alertType, optionLabel);
    }

    public getDeliveryMethodFilterOptions<T extends {deliveryMethod: string}>(items: T[], optionLabel: FilterOptionEnumLabel): void {
        this.getEnumStringOptions(this.deliveryMethodFilter, items, (item: T) => item.deliveryMethod, optionLabel);
    }

    /**
     * Applies the status filter on a set of items that have a status property
     * @param items A list of items
     * @param getItemOptionKey A method for extracting the status key
     */
    private applyStatusFilter<T extends {status: string}>(items: T[]): T[] {
        return this.applyGenericStringOptions(this.statusFilter, items, (item: T) => item.status);
    }

    private applyAlertTypeFilter<T extends {alertType: string}>(items: T[]): T[] {
        return this.applyGenericStringOptions(this.alertTypeFilter, items, (item: T) => item.alertType);
    }

    private applyDeliveryMethodFilter<T extends {deliveryMethod: string}>(items: T[]): T[] {
        return this.applyGenericStringOptions(this.deliveryMethodFilter, items, (item: T) => item.deliveryMethod);
    }

    /**
     * Gets the list of type options from a list of items that have a type
     * @param items A list of items
     * @param localePrefix A prefix for localizing the options
     */
    private getTypeFilterOptions<T extends {type: string}>(items: T[], optionLabel: FilterOptionEnumLabel): void {
        this.getEnumStringOptions(this.typeFilter, items, (item: T) => item.type, optionLabel);
    }

    public getAutoSavingFilterOptions<T>(autoSavingFilter: FilterTypeRadioModel<ListEntitiesVisibilityEnum>): void {
        autoSavingFilter.options = [
            {
                label: 'Monitoring.filter.autoSaving.all',
                value: ListEntitiesVisibilityEnum.all
            },
            {
                label: 'Monitoring.filter.autoSaving.visible',
                value: ListEntitiesVisibilityEnum.visible
            },
            {
                label: 'Monitoring.filter.autoSaving.hidden',
                value: ListEntitiesVisibilityEnum.hidden
            }
        ];
    }


    /**
     * Applies the type filter on a set of items that have a type property
     * @param items A list of items
     */
    public applyTypeFilter<T extends {type: string}>(items: T[], typeFilter: FilterTypeCheckboxModel<string> = this.typeFilter): T[] {
        return this.applyGenericStringOptions(typeFilter, items, (item: T) => item.type);
    }

    /**
     * Refreshes the Tag filter options when the tags list gets updated
     */
    refreshTagFilterOptions(): void {
        if (!this.filters.includes(this.autoTaggingFilter)) {
            return;
        }

        switch (this.currentMode) {
            case Mode.topics:
                this.getTagFilterOptions(this.autoTaggingFilter, this.topics, this.getAutoTagIdsFromTopic);
                break;
            case Mode.mediaSubscriptions:
                break;
            case Mode.mentions:
                break;
            case Mode.briefings:
                break;
            case Mode.alerts:
                break;
            default:
                break;
        }
    }

    /**
     * Registering all non-static data with the configMap.
     * The config needs a reference to list items, and needs a way to
     * convert those list items to LeftSidebarListItems. Sometimes that conversion
     * including specific logic for handling items that are always present (e.g., All Mentions)
     */
    private registerLeftSidebarConfigs() {
        this.monitoringLeftSidebarModeConfigMap.registerConfig(
            Mode.topics,
            {
                listItemSource: this.topics,
                listItemStream: gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
                    switchMap(() => this.topicsService.getTopics())
                ),
                convertToSidebarListItems: (items: MentionSearchListItemModel[]) => {
                    const buttons = [
                        this.itemActionRename,
                        this.itemActionEdit,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                    return items.map(item => item.toLeftSidebarListItem(buttons));
                },
            }
        );
        this.monitoringLeftSidebarModeConfigMap.registerConfig(
            Mode.newSearch,
            {
                listItemSource: this.topics,
                listItemStream: gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
                    switchMap(() => this.topicsService.getTopics())
                ),
                convertToSidebarListItems: (items: MentionSearchListItemModel[]) => {
                    const buttons = [
                        this.itemActionRename,
                        this.itemActionEdit,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                    return items.map(item => item.toLeftSidebarListItem(buttons));
                },
            }
        );
        this.monitoringLeftSidebarModeConfigMap.registerConfig(
            Mode.mentions,
            {
                listItemSource: this.folders,
                listItemStream: gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
                    switchMap(() => this.folderService.all$)
                ),
                convertToSidebarListItems: (items: Folder[]) => {
                    const buttons = [
                        this.itemActionRename,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];

                    const convertedItems = items.map(item => item.toLeftSidebarListItem(buttons));
                    return convertedItems;
                }
            },
        );
        this.monitoringLeftSidebarModeConfigMap.registerConfig(
            Mode.briefings,
            {
                listItemSource: this.briefings,
                listItemStream: this.briefingsService.getBriefingListItems(),
                convertToSidebarListItems: (items: BriefingListItemModel[]) => {
                    const buttons = [
                        this.itemActionRename,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];

                    return items.map(item => {
                        const listItem = item.toLeftSidebarListItem(buttons);
                        if (item.status === BriefingStatusEnum.ACTIVE || item.status === BriefingStatusEnum.PAUSED) {
                            listItem.secondaryOption = 'Briefings.secondaryOption.label';
                        }
                        if (item.id === this.briefingsService.currentBriefingId && this.briefingsService.isBlockLimitExceeded) {
                            listItem.indicator = 'fa-solid fa-circle-exclamation agpr-leftSidebarItems-itemIcon-blockLimit';
                            listItem.indicatorTooltip = this.localizationService.transform('Briefing.totalBlocks.limitExceeded.tooltip', {
                                number: this.briefingsService.totalBlockCount
                            });
                        }
                        return listItem;
                    });
                },
            },
        );
        this.monitoringLeftSidebarModeConfigMap.registerConfig(
            Mode.alerts,
            {
                listItemSource: this.alerts,
                listItemStream: this.alertsService.getAlertsList(),
                convertToSidebarListItems: (items: BriefingListItemModel[]) => {
                    const buttons = [
                        this.itemActionRename,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];
                    return items
                        .map(item => {
                            const convertedItem = item.toLeftSidebarListItem(buttons);
                            convertedItem.status = `Alerts.status.${item.status}`;
                            if (item.alertType === AlertTypeEnum.SURGE &&
                                (item.status === BriefingStatusEnum.ACTIVE || item.status === BriefingStatusEnum.PAUSED)) {
                                convertedItem.secondaryOption = 'Briefings.secondaryOption.label';
                            }
                            return convertedItem;
                        });
                },
            },
        );
        this.monitoringLeftSidebarModeConfigMap.registerConfig(
            Mode.mediaSubscriptions,
            {
                listItemSource: this.mediaSubscriptions,
                listItemStream: this.mediaSubscriptionService.all$,
                convertToSidebarListItems: (items: MediaSubscription[]) => {
                    if (!this.mediaSubscriptionsProvisioned) {
                        return [];
                    }

                    const buttons = [
                        this.itemActionRename,
                        this.itemActionEdit,
                        this.itemActionCopy,
                        this.itemActionFavorite,
                        this.itemActionUnfavorite,
                        this.itemActionMove,
                        this.itemActionHide,
                        this.itemActionShow,
                        this.itemActionDelete
                    ];

                    const convertedItems = items.map(item => item.toLeftSidebarListItem(buttons));
                    return convertedItems;
                }
            },
        );
    }

    /**
     * Applies the current set of filters to the list items
     */
    applyFilters(): void {
        super.applyFilters();

        const config = this.monitoringLeftSidebarModeConfigMap.getConfig(this.currentMode);
        let filteredItems: GenericLeftSidebarItem[] = config.listItemSource.slice();

        if (!filteredItems) {
            this.listItems = null;
            this.listItemsProvider.next(this.listItems);

            return;
        }

        filteredItems = this.applyNameFilters(filteredItems);
        filteredItems = this.applyAuditableFilters(filteredItems);

        if (this.filters.includes(this.autoTaggingFilter)) {
            this.autoTaggingFilter.setIncludeExclude(this.autoTaggingFilter.currentIncludeExclude);
            filteredItems = this.applyTagsFilter(this.autoTaggingFilter, filteredItems, this.getAutoTagIdsFromTopic, this.getTopicsWithAutoTagging);
        }

        if (this.filters.includes(this.statusFilter)) {
            filteredItems = this.applyStatusFilter(filteredItems as StatusLeftSidebarItem[]);
        }
        if (this.filters.includes(this.alertTypeFilter)) {
            filteredItems = this.applyAlertTypeFilter(filteredItems as AlertTypeLeftSidebarItem[]);
        }

        if (this.filters.includes(this.deliveryMethodFilter)) {
            filteredItems = this.applyDeliveryMethodFilter(filteredItems as BriefingDeliveryMethodLeftSidebarItem[]);
        }
        if (this.filters.includes(this.typeFilter)) {
            filteredItems = this.applyTypeFilter(filteredItems as TypeLeftSidebarItem[]);
        }
        if (this.filters.includes(this.autoSavingFilter)) {
            filteredItems = this.applyAutoSavingFilter(filteredItems as MentionSearchListItemModel[]);
        }

        const allVisibiltyItems = filteredItems.slice();
        const hiddenItems = this.getHiddenItemsFromFilteredItems(this.visibilityFilter, allVisibiltyItems);
        this.hiddenListItems = config.convertToSidebarListItems(hiddenItems)
            .filter(item => {
                // Exclude the "ALL XYZ" top items which should always be shown
                return (
                    item.value !== this.allMentionsKey
                    || item.value !== this.subscriptionsDashboardKey
                );
            });
        this.hiddenListItemsProvider.next(this.hiddenListItems);

        filteredItems = this.applyVisibilityFilter(this.visibilityFilter, filteredItems);
        this.listItems = this.markLiveAgentSources(config.convertToSidebarListItems(filteredItems));
        this.groupListItems();
        this.listItemsProvider.next(this.listItems);
    }

    /**
     * Flags the rows an automation agent is live on. Only the modes that list something an agent
     * can watch have a type to look up, so the rest pass straight through.
     */
    private markLiveAgentSources(items: LeftSidebarListItem[]): LeftSidebarListItem[] {
        const sourceType = modeToAgentSourceTypeMap.get(this.currentMode);

        if (!sourceType) {
            return items;
        }

        // Every row is reassigned, even when nothing is live, so a source whose last agent was just
        // paused loses its marker rather than keeping the one from before
        items.forEach(item => {
            const count = this.liveAgentCounts.get(this.agentSourceIndexService.key(sourceType, item.value));

            item.leadingIcon = count ? 'fa-solid fa-signal-stream' : null;
            item.leadingIconTooltip = count
                ? this.localizationService.transform('AutomationAgents.liveAgent', { count: `${count}` })
                : null;
        });

        return items;
    }

    updateSortFields(mode: Mode){
        // reset the sort field to name and order to ascending after mode change
        this.sortOrder = SortOrderEnum.ascending;
        this.sortOrder$.next(this.sortOrder);
        this.selectedSortField = this.sortFieldName;
        this.selectedSortField$.next(this.selectedSortField);

        if (mode === Mode.briefings || mode === Mode.mediaSubscriptions || mode === Mode.alerts){
            this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldStatus];
        }else if (mode === Mode.mentions){
            this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate, this.sortFieldResult];
        }else{
            this.lhpSortFields = [this.sortFieldName, this.sortFieldCreatedDate];
        }
        this.lhpSortFields$.next(this.lhpSortFields);
    }

    /**
     * Based on the current mode, returns an observable that sets and prepares the current filters.
     */
    private getItemsAndPrepareFilters(currentMode: Mode): Observable<void> {
        const config = this.monitoringLeftSidebarModeConfigMap.getConfig(currentMode);
        const getItems$ = config.listItemStream as Observable<LeftSidebarListItemConvertableModel[]>;
        const type = this.modeToGroupTypeMap.get(this.currentMode);
        // Modes without a display group type have no groups to fetch
        const getGroups$ = type === undefined ? of([]) : this.displayGroupsService.setTypeAndGetGroups(type);

        // When the list items emit, we call prepareFilters(). prepareFilters causes an inner subscription in
        // getAuditableFilterOptions. We need a combineLatest here (instead of simple piping) because of this
        // (We need to call the function but also have a subscription set up to handle it if it happens synchronously)
        // TODO: Clean up the getAuditableFilterOptions method to allow easier composition
        return combineLatest([
            this.auditableFilterOptionsComplete$.pipe(
                take(1)
            ),
            getItems$.pipe(
                rxjsFilter(x => !!x),
                tap(() => {
                    this.setFilters(config.filters);
                    config.prepareFilterLabels();
                    this.prepareConfigFilters(config);
                })
            ),
            getGroups$.pipe(
                map((groups) => {
                    this.listItemGroups = groups.slice();
                })
            )
        ]).pipe(
            mapTo(null),
        );
    }
    //#endregion

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
